/**
 * TopStatusBar + StatusCluster.
 *
 * SPLIT into two named exports per user brief:
 *   • <TopStatusBar>    — left caption "FOUNDER CONSOLE • PHASE 0",
 *                         renders inside main (center column).
 *   • <StatusCluster>   — SYS.<state> + LINK.<state> + session timer +
 *                         audio glyph. Renders at the TOP of the right
 *                         column, right-aligned with the transcript
 *                         card below it (no overlap/crowding).
 */

import { motion, AnimatePresence } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import SessionTimer from "@/components/SessionTimer";
import ProjectSelector from "@/components/ProjectSelector";

const AGENT_LABEL = {
    idle: "SYS.IDLE",
    connecting: "SYS.CONNECTING",
    listening: "SYS.LISTENING",
    thinking: "SYS.THINKING",
    speaking: "SYS.SPEAKING",
    error: "SYS.ERROR",
};
const LINK_LABEL = {
    new: "LINK.READY",
    connecting: "LINK.CONNECTING",
    connected: "LINK.STABLE",
    disconnecting: "LINK.CLOSING",
    disconnected: "LINK.DISCONNECTED",
    failed: "LINK.FAILED",
    closed: "LINK.IDLE",
};
const ACTIVE_STATES = new Set(["listening", "thinking", "speaking"]);
const LINK_ACTIVE = new Set(["connected"]);

function Dot({ active, tone = "cyan" }) {
    const color = tone === "mint" ? "#5EF7C6" : "#00E5FF";
    return (
        <span
            className="inline-block h-1.5 w-1.5 rounded-full"
            style={{
                backgroundColor: active ? color : "#52525B",
                boxShadow: active ? `0 0 8px ${color}` : "none",
            }}
        />
    );
}

/** Caption only — sits in the center column top. */
export function TopStatusBar() {
    return (
        <header
            data-testid="top-status-bar"
            className="flex items-center px-6 pt-4 pb-2 shrink-0 gap-3"
        >
            <div className="flex items-center gap-2 min-w-0">
                <span
                    className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-qoryn-textDim"
                    data-testid="console-title"
                >
                    Founder Console
                </span>
                <span className="text-qoryn-textMuted">•</span>
                <span
                    className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-qoryn-cyan/80"
                    data-testid="phase-tag"
                >
                    Phase 1B
                </span>
            </div>
            <div className="flex-1" />
            <ProjectSelector compact />
        </header>
    );
}

/** Right-aligned cluster of live session indicators. */
export function StatusCluster({
    agentState,
    connectionState,
    sessionStartedAt,
    outputMuted = false,
    onToggleOutputMute,
}) {
    return (
        <div
            data-testid="status-cluster"
            data-agent-state={agentState}
            data-connection-state={connectionState}
            className="flex items-center justify-end gap-3 shrink-0 self-end flex-nowrap whitespace-nowrap"
        >
            <div
                data-testid="state-indicator"
                className="flex items-center gap-3 qoryn-panel rounded-full px-3 py-1.5"
            >
                <div className="flex items-center gap-1.5">
                    <Dot active={ACTIVE_STATES.has(agentState)} />
                    <AnimatePresence mode="wait">
                        <motion.span
                            key={agentState}
                            initial={{ opacity: 0, y: 3 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -3 }}
                            transition={{ duration: 0.2 }}
                            className="font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-text"
                            data-testid="state-indicator-agent-label"
                        >
                            {AGENT_LABEL[agentState] ?? "SYS.IDLE"}
                        </motion.span>
                    </AnimatePresence>
                </div>
                <span className="h-3 w-px bg-white/10" />
                <div className="flex items-center gap-1.5">
                    <Dot active={LINK_ACTIVE.has(connectionState)} tone="mint" />
                    <span
                        className="font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-textDim"
                        data-testid="state-indicator-link-label"
                    >
                        {LINK_LABEL[connectionState] ?? "LINK.IDLE"}
                    </span>
                </div>
            </div>
            <SessionTimer startedAt={sessionStartedAt} />
            <button
                type="button"
                data-testid="output-speaker-btn"
                data-output-muted={outputMuted ? "true" : "false"}
                onClick={onToggleOutputMute}
                aria-pressed={outputMuted}
                aria-label={outputMuted ? "Unmute Qoryn output" : "Mute Qoryn output"}
                title={outputMuted
                    ? "Qoryn output muted — click to unmute"
                    : "Mute Qoryn output audio"}
                className={`qoryn-panel rounded-full h-8 w-8 flex items-center justify-center transition-colors duration-150 qoryn-focus ${
                    outputMuted
                        ? "text-red-300 border-red-400/40"
                        : "text-qoryn-textDim hover:text-qoryn-text"
                }`}
            >
                {outputMuted ? (
                    <VolumeX className="h-3.5 w-3.5" strokeWidth={1.75} />
                ) : (
                    <Volume2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                )}
            </button>
        </div>
    );
}

export default TopStatusBar;
